import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { fetchOrderDetails, approveOrder, downloadOrderPDF, fetchOrderImages, fetchOrderPaymentStatus } from '../services/order';
import {
  setOrderDetails,
  setDetailsLoading,
  updateOrderPaymentStatus,
} from '../store/orderSlice';
import productImage from '../assets/images/product.svg';
import badgeIcon from '../assets/icons/badge.svg';
import { roundMoneyToCents, stripWebInfoTitle } from '../utils/helpers';

export default function OrderDetails() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderDetails = useSelector((state) => state.order.orderDetails);
  const detailsLoading = useSelector((state) => state.order.detailsLoading);

  const [approving, setApproving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPurchasing, setDownloadingPurchasing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [hoveredSupplementId, setHoveredSupplementId] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const supplementRefs = useRef({});
  const [productImages, setProductImages] = useState({});
  const pollingIntervalRef = useRef(null);
  const pollingTimeoutRef = useRef(null);
  const cancelledRef = useRef(false);
  const pollCountRef = useRef(0);

  useEffect(() => {
    if (!orderId) return;

    let cancelled = false;

    const loadOrderDetails = async () => {
      try {
        dispatch(setDetailsLoading(true));
        const data = await fetchOrderDetails(orderId);
        if (!cancelled) {
          dispatch(setOrderDetails(data));
        }
      } catch (error) {
        if (!cancelled) {
          dispatch(setDetailsLoading(false));
        }
      }
    };

    loadOrderDetails();

    return () => {
      cancelled = true;
    };
  }, [orderId, dispatch]);

  // Fetch product images after order details are loaded
  useEffect(() => {
    if (!orderId || !orderDetails || detailsLoading) return;

    let cancelled = false;

    const loadImages = async () => {
      try {
        const images = await fetchOrderImages(orderId);
        if (!cancelled && images && Array.isArray(images)) {
          // Create a map of SKU to image URL (normalize SKU to string for consistent matching)
          const imageMap = {};
          images.forEach((item) => {
            if (item.sku && item.image_url) {
              const normalizedSku = String(item.sku);
              imageMap[normalizedSku] = item.image_url;
            }
          });
          setProductImages(imageMap);
        }
      } catch (error) {
        // Silently fail - images are optional
      }
    };

    loadImages();

    return () => {
      cancelled = true;
    };
  }, [orderId, orderDetails, detailsLoading]);

  useEffect(() => {
    const redirectParam = searchParams.get('redirect');
    if (redirectParam === 'true' && orderDetails) {
      const paymentStatus = orderDetails.payment_status?.toLowerCase();
      
      if (paymentStatus === 'open' || paymentStatus === 'pending' || paymentStatus === 'authorized') {
        toast.info('System is processing the payment, please wait');
      } else if (paymentStatus === 'failed' || paymentStatus === 'canceled') {
        const displayStatus = paymentStatus === 'canceled' ? 'cancelled' : paymentStatus;
        toast.error(`Your payment is ${displayStatus}, please initiate payment again`);
      } else if (paymentStatus === 'paid') {
        toast.success('Payment is approved');
      }
      
      searchParams.delete('redirect');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, orderDetails]);

  useEffect(() => {
    if (!orderId || !orderDetails || detailsLoading) return;
    
    const paymentStatus = orderDetails.payment_status?.toLowerCase();
    const webhookReceived = orderDetails.webhook_recieved;
    const pollingStatuses = ['open', 'pending', 'authorized'];
    
    // Only poll if payment_status is in polling list and webhook not received
    if (paymentStatus && pollingStatuses.includes(paymentStatus) && webhookReceived === false) {
      cancelledRef.current = false;
      pollCountRef.current = 0;
      
      const pollPaymentStatus = async () => {
        if (cancelledRef.current) {
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return;
        }
        
        const currentPollCount = pollCountRef.current || 0;
        const maxPolls = 90; // 900 seconds / 10 seconds = 90 polls
        
        if (currentPollCount >= maxPolls) {
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
          }
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          return;
        }
        
        try {
          const response = await fetchOrderPaymentStatus(orderId);
          
          if (cancelledRef.current) return;
          
          pollCountRef.current = (pollCountRef.current || 0) + 1;
          
          if (response.webhook_recieved === true) {
            // Webhook received, update payment status, approval, and shipment status
            const finalPaymentStatus = response.payment_status;
            dispatch(updateOrderPaymentStatus({
              orderId,
              paymentStatus: finalPaymentStatus,
              webhookReceived: true,
              isApproved: response.is_approved,
              shipmentStatus: response.shipment_status,
            }));
            
            // Show toast based on final payment status
            const status = finalPaymentStatus?.toLowerCase();
            if (status === 'paid') {
              toast.success('Payment is approved');
            } else if (status === 'failed' || status === 'canceled') {
              const displayStatus = status === 'canceled' ? 'cancelled' : status;
              toast.error(`Your payment is ${displayStatus}, please initiate payment again`);
            } else if (status === 'expired') {
              toast.error('Payment has expired, please initiate payment again');
            }
            
            // Stop polling
            if (pollingTimeoutRef.current) {
              clearTimeout(pollingTimeoutRef.current);
              pollingTimeoutRef.current = null;
            }
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          } else if (pollCountRef.current >= maxPolls) {
            // Max polls reached, stop polling
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        } catch (error) {
          // Continue polling on error
        }
      };
      
      // Start polling after 10 seconds, then continue every 10 seconds
      pollingTimeoutRef.current = setTimeout(() => {
        pollPaymentStatus();
        pollingIntervalRef.current = setInterval(pollPaymentStatus, 10000);
      }, 10000);
    } else {
      // Clean up polling if status changed or webhook received
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
    
    // Cleanup function
    return () => {
      cancelledRef.current = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      pollCountRef.current = 0;
    };
  }, [orderId, orderDetails, detailsLoading, dispatch]);

  const transformOrderData = (apiData, imageMap = {}) => {
    if (!apiData) return null;

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB').replace(/\//g, '.');
    };

    const convertDateToEUFormat = (dateString) => {
      if (!dateString) return 'N/A';
      const parts = dateString.split(/[-.]/);
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return `${day}.${month}.${year}`;
      }
      return dateString;
    };

    const formatPrice = (price) => {
      return price.replace(/\./g, ',');
    };
    const toNumber = (value) => {
      if (value == null || value === '') return 0;
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
      const normalized = String(value)
        .trim()
        .replace(/[^0-9,.-]/g, '')
        .replace(',', '.');
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const computedTotalFromLines = (apiData.prescriptions || []).reduce(
      (sum, prescription) => {
        const unitPrice = toNumber(prescription?.product?.price);
        const qty = toNumber(prescription?.quantity || 1);
        return sum + unitPrice * qty;
      },
      0
    );
    const fallbackApiTotal = toNumber(apiData.price);

    return {
      orderId: apiData.order_id || orderId,
      bloodTestId: String(
        apiData.blood_test_id ??
          apiData.bloodTestId ??
          apiData.test_id ??
          ''
      ).trim(),
      blood_test_id: String(
        apiData.blood_test_id ??
          apiData.bloodTestId ??
          apiData.test_id ??
          ''
      ).trim(),
      orderInfo: {
        id: apiData.order_id || orderId,
        dateAdded: formatDate(apiData.created_at),
        dateOrdered: convertDateToEUFormat(apiData.ordered_date),
        shipmentStatus: apiData.shipment_status || 'N/A',
        paymentStatus: apiData.payment_status || null,
        approval: apiData.is_approved || false,
        trackingNumbers: apiData.tracking_numbers || [],
        totalProductCount: apiData.total_product_count || 0,
        deliveredProductCount: apiData.delivered_product_count || 0,
      },
      customerInfo: {
        title: apiData.customer_info?.title || '',
        firstName: apiData.customer_info?.first_name || '',
        lastName: apiData.customer_info?.last_name || '',
        address1: apiData.customer_info?.address_line_1 || '',
        streetNumber: apiData.customer_info?.street_number || '',
        city: apiData.customer_info?.city || '',
        postalCode: apiData.customer_info?.postal_code || '',
        country: apiData.customer_info?.country || '',
        email: apiData.customer_info?.shipping_email || '',
      },
      supplements: (apiData.prescriptions || []).map((prescription, index) => {
        const content = parseInt(prescription.product?.content, 10) || '';
        const contentUnit = prescription.product?.content_unit || '';
        const description =
          content && contentUnit
            ? `${content} ${contentUnit}`
            : content || contentUnit || '';
        // Extract SKU - handle both string and number formats
        const rawSku = prescription.product?.sku || prescription.product?.product_sku;
        const sku = rawSku ? String(rawSku) : '';
        
        // Use fetched image if available, otherwise use placeholder
        const productImageUrl = (sku && imageMap[sku]) || productImage;

        return {
          id: index + 1,
          name: prescription.product?.name || 'N/A',
          brand: prescription.product?.brand || '',
          description: description,
          webInfo: prescription.product?.web_info || '',
          isComplex: prescription.product?.is_complex || false,
          substance: prescription.substance?.substance_name || 'N/A',
          dosage: prescription.dosage || '',
          form: prescription.form || '',
          frequency: prescription.daily_frequency?.toString() || '',
          numberOfDays: prescription.number_of_days?.toString() || '',
          quantity: prescription.quantity?.toString() || 1,
          price: prescription.product?.price
            ? formatPrice(`€${parseFloat(prescription.product.price).toFixed(2)}`)
            : '€0,00',
          detailPrice: prescription.product?.detail_price || '0',
          sku: sku,
          image: productImageUrl,
        };
      }),
      purchasingAmount: apiData.purchase_price != null && apiData.purchase_price !== ''
        ? toNumber(apiData.purchase_price)
        : (apiData.prescriptions || []).reduce((sum, prescription) => {
            const detailPrice = toNumber(prescription.product?.detail_price) * toNumber(prescription.quantity || 1);
            return sum + detailPrice;
          }, 0),
      total: computedTotalFromLines > 0 || fallbackApiTotal > 0
        ? formatPrice(`€${(computedTotalFromLines > 0 ? computedTotalFromLines : fallbackApiTotal).toFixed(2)}`)
        : '€0,00',
    };
  };

  const order = transformOrderData(orderDetails, productImages);
  const SHIPPING_AND_FULFILMENT = 7.234;
  const toNumber = (value) => {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const normalized = String(value)
      .trim()
      .replace(/[^0-9,.-]/g, '')
      .replace(',', '.');
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const formatEuroNl = (amount, fractionDigits = 2) =>
    `€${Number(amount).toFixed(fractionDigits).replace('.', ',')}`;

  const shouldHideApproveButton = order?.orderInfo?.paymentStatus && 
    ['paid', 'authorized', 'pending', 'open'].includes(order.orderInfo.paymentStatus.toLowerCase());

  const hasBloodTestId = Boolean(String(order?.bloodTestId || '').trim());
  const showEditButton = !order?.orderInfo?.approval || hasBloodTestId;

  const handleEditOrder = () => {
    navigate('/prescription', {
      state: {
        editOrder: {
          orderId: order.orderId,
          clientInfo: {
            title: order.customerInfo.title || '',
            firstName: order.customerInfo.firstName || '',
            lastName: order.customerInfo.lastName || '',
          },
          shippingInfo: {
            address1: order.customerInfo.address1 || '',
            streetNumber: order.customerInfo.streetNumber || '',
            postalCode: order.customerInfo.postalCode || '',
            city: order.customerInfo.city || '',
            country: order.customerInfo.country || '',
            email: order.customerInfo.email || '',
          },
          prescriptions: order.supplements.map((sup) => ({
            substance: sup.substance,
            dosage: sup.dosage,
            form: sup.form,
            frequency: sup.frequency,
            days: sup.numberOfDays,
          })),
        },
      },
    });
  };

  if (detailsLoading) {
    return (
      <div className="w-full bg-[#F9F5F2]">
        <div className="min-h-[calc(100vh-80px)] py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin h-10 w-10 border-4 border-[#C1DCD4] border-t-[#119975] rounded-full" />
                <span className="text-[#2B3133] font-poppins text-sm">
                  Loading order details...
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order || !orderDetails) {
    return (
      <div className="w-full bg-[#F9F5F2]">
        <div className="min-h-[calc(100vh-80px)] py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6">
              <button
                onClick={() => navigate('/order-history')}
                className="flex items-center gap-2 text-gray-500 hover:text-[#119975] transition-colors font-poppins text-xs mb-6"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.5 3L2 8L6.5 13M2.5 8H14"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                All orders
              </button>
            </div>
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <p className="text-gray-600 font-poppins">
                  Order not found or failed to load.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const productsSubtotal = toNumber(order.total);
  const shippingAndFulfilment = productsSubtotal > 0 ? SHIPPING_AND_FULFILMENT : 0;
  // Round ex-VAT shipping first, then BTW on that line — matches common invoice PDF logic and avoids €0,01 drift.
  const shippingExVat = roundMoneyToCents(shippingAndFulfilment);
  const vatOnShippingAndFulfilment = roundMoneyToCents(shippingExVat * 0.21);
  const grandTotal = roundMoneyToCents(
    productsSubtotal + shippingExVat + vatOnShippingAndFulfilment
  );

  const parseTrackingNumber = (trackingNumber) => {
    if (!trackingNumber) return '';
    // Extract the tracking number part (before space or "PostNL")
    const match = trackingNumber.trim().match(/^([^\s]+)/);
    return match ? match[1] : trackingNumber.trim();
  };

  const buildTrackingUrl = (trackingNumber, postalCode) => {
    const parsedTracking = parseTrackingNumber(trackingNumber);
    const postalCodeWithoutSpaces = (postalCode || '').replace(/\s+/g, '');
    const countryCode = 'NL';
    return `https://jouw.postnl.nl/track-and-trace/${parsedTracking}-${countryCode}-${postalCodeWithoutSpaces}`;
  };

  const handleApproveClick = () => {
    setShowConfirmModal(true);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
  };

  const handleApprove = async () => {
    if (!orderId) return;

    setShowConfirmModal(false);

    try {
      setApproving(true);

      const hasBloodTestId = Boolean(String(order?.bloodTestId || '').trim());
      const response = await approveOrder(
        orderId,
        hasBloodTestId ? { initiatePayment: false } : undefined
      );

      if (
        response.success &&
        response.data &&
        response.data.message &&
        response.data.message.includes('out of stock') &&
        !response.data.is_approved
      ) {
        toast.warning(response.data.message);

        dispatch(setDetailsLoading(true));
        const updatedData = await fetchOrderDetails(orderId);
        dispatch(setOrderDetails(updatedData));
        return;
      }

      if (
        response.success &&
        response.data &&
        response.data.checkout_url
      ) {
        if (hasBloodTestId) {
          dispatch(
            updateOrderPaymentStatus({
              orderId,
              paymentStatus: response.data.payment_status,
              isApproved:
                response.data.is_approved !== undefined
                  ? response.data.is_approved
                  : true,
              shipmentStatus: response.data.shipment_status,
            })
          );
          toast.success('Order approved successfully');
          return;
        }
        toast.info('Redirecting to payment...');
        window.location.href = response.data.checkout_url;
        return;
      }

      if (
        response.success &&
        response.data &&
        (response.data.payment_status || response.data.is_approved !== undefined || response.data.shipment_status)
      ) {
        dispatch(updateOrderPaymentStatus({
          orderId,
          paymentStatus: response.data.payment_status,
          isApproved: response.data.is_approved,
          shipmentStatus: response.data.shipment_status,
        }));
        toast.success('Order approved successfully');
      }
    } catch (error) {
      const errorMessage =
        error.message || 'Failed to approve order. Please try again.';
      toast.error(errorMessage);
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!orderId) return;

    try {
      setDownloading(true);
      await downloadOrderPDF(orderId, 'client');
      toast.success('Client invoice downloaded successfully!');
    } catch (error) {
      const errorMessage =
        error.message || 'Failed to download client invoice. Please try again.';
      toast.error(errorMessage);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPurchasingInvoice = async () => {
    if (!orderId) return;

    try {
      setDownloadingPurchasing(true);
      await downloadOrderPDF(orderId, 'purchasing');
      toast.success('Purchasing invoice downloaded successfully!');
    } catch (error) {
      const errorMessage =
        error.message || 'Failed to download purchasing invoice. Please try again.';
      toast.error(errorMessage);
    } finally {
      setDownloadingPurchasing(false);
    }
  };

  return (
    <div className="w-full bg-[#F9F5F2]">
      <div className="min-h-[calc(100vh-80px)] py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <button
              onClick={() => navigate('/order-history')}
              className="flex items-center gap-2 text-gray-500 hover:text-[#119975] transition-colors font-poppins text-xs mb-6"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.5 3L2 8L6.5 13M2.5 8H14"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              All orders
            </button>
            <h1 className="text-[#2B3133] font-poppins font-medium text-[18px] md:text-[20px] leading-[1.1em]">
              Order #{order.orderId}
            </h1>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-6 md:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <h2 className="text-[#2B3133] font-poppins font-medium text-lg">
                  Order Info
                </h2>
                {order.orderInfo.approval && (
                  <span className="inline-flex items-center px-3 py-0.5 rounded-md text-[11px] font-medium bg-[#E7F6EF] text-[#119975] font-poppins">
                    Approved
                  </span>
                )}
                {!order.orderInfo.approval && (
                  <span className="inline-flex items-center px-3 py-0.5 rounded-md text-[11px] font-medium bg-gray-100 text-gray-600 font-poppins">
                    Awaiting Approval
                  </span>
                )}
              </div>
              <div className="space-y-4 font-poppins text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">ID:</span>
                  <span className="text-gray-900 text-xs font-medium">
                    {order.orderInfo.id}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">Date added:</span>
                  <span className="text-gray-900 text-xs font-medium">
                    {order.orderInfo.dateAdded}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">Date ordered:</span>
                  <span className="text-gray-900 text-xs font-medium">
                    {order.orderInfo.dateOrdered}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">
                    Order/Shipment status:
                  </span>
                  <span
                    className={`inline-flex items-center px-3 py-0.5 rounded-md text-[11px] font-medium font-poppins ${
                      order.orderInfo.shipmentStatus === 'Delivered'
                        ? 'bg-[#E7F6EF] text-[#119975]'
                        : order.orderInfo.shipmentStatus ===
                              'Order is shipped' ||
                            order.orderInfo.shipmentStatus ===
                              'Preparing for shipment'
                          ? 'bg-[#FFF4E6] text-[#FF8A3C]'
                          : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {order.orderInfo.shipmentStatus}
                  </span>
                </div>
                {order.orderInfo.paymentStatus && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-xs">
                      Payment Status:
                    </span>
                    <span
                      className={`inline-flex items-center px-3 py-0.5 rounded-md text-[11px] font-medium font-poppins ${
                        order.orderInfo.paymentStatus === 'paid'
                          ? 'bg-[#E7F6EF] text-[#119975]'
                          : order.orderInfo.paymentStatus === 'open' ||
                            order.orderInfo.paymentStatus === 'pending' ||
                            order.orderInfo.paymentStatus === 'authorized'
                          ? 'bg-[#FFF4E6] text-[#FF8A3C]'
                          : order.orderInfo.paymentStatus === 'failed' ||
                            order.orderInfo.paymentStatus === 'canceled' ||
                            order.orderInfo.paymentStatus === 'expired'
                          ? 'bg-red-100 text-red-600'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {order.orderInfo.paymentStatus === 'canceled'
                        ? 'Cancelled'
                        : order.orderInfo.paymentStatus.charAt(0).toUpperCase() + order.orderInfo.paymentStatus.slice(1)}
                    </span>
                  </div>
                )}
                {order.orderInfo.trackingNumbers &&
                  order.orderInfo.trackingNumbers.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-xs whitespace-nowrap">
                        Tracking number:
                      </span>
                      <span className="text-gray-900 text-xs font-medium break-words">
                        {order.orderInfo.trackingNumbers.map((trackingNum, index) => {
                          const url = buildTrackingUrl(
                            trackingNum,
                            order.customerInfo.postalCode
                          );
                          return (
                            <span key={index}>
                              {index > 0 && ', '}
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#119975] hover:underline"
                              >
                                {trackingNum}
                              </a>
                            </span>
                          );
                        })}
                      </span>
                    </div>
                  )}
                {order.orderInfo.approval && (
                  <div className="flex items-center gap-2">
                    <span className="text-gray-500 text-xs whitespace-nowrap">
                      Delivered Products:
                    </span>
                    <span className="text-gray-900 text-xs font-medium">
                      {order.orderInfo.deliveredProductCount}/{order.orderInfo.totalProductCount}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 md:col-span-2">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-[#2B3133] font-poppins font-medium text-lg">
                  Customer Info
                </h2>
                {showEditButton && (
                  <button
                    onClick={handleEditOrder}
                    className="flex items-center gap-2 text-gray-600 hover:text-[#119975] font-medium transition-colors font-poppins text-xs px-4 py-2 border border-gray-300 rounded-lg hover:border-[#119975]"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M11.334 2.00004C11.5091 1.82494 11.7169 1.68605 11.9457 1.59129C12.1745 1.49653 12.4197 1.44775 12.6673 1.44775C12.9149 1.44775 13.1601 1.49653 13.3889 1.59129C13.6177 1.68605 13.8256 1.82494 14.0007 2.00004C14.1757 2.17513 14.3146 2.383 14.4094 2.61178C14.5042 2.84055 14.5529 3.08575 14.5529 3.33337C14.5529 3.58099 14.5042 3.82619 14.4094 4.05497C14.3146 4.28374 14.1757 4.49161 14.0007 4.66671L5.00065 13.6667L1.33398 14.6667L2.33398 11L11.334 2.00004Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Edit
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 font-poppins text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">First Name:</span>
                  <span className="text-gray-900 text-xs font-medium">
                    {order.customerInfo.firstName}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">Postal code:</span>
                  <span className="text-gray-900 text-xs font-medium">
                    {order.customerInfo.postalCode}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">Last Name:</span>
                  <span className="text-gray-900 text-xs font-medium">
                    {order.customerInfo.lastName}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">City:</span>
                  <span className="text-gray-900 text-xs font-medium">
                    {order.customerInfo.city}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">Street Name:</span>
                  <span className="text-gray-900 text-xs font-medium">
                    {order.customerInfo.address1}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">Country:</span>
                  <span className="text-gray-900 text-xs font-medium">
                    {order.customerInfo.country}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">Street Number:</span>
                  <span className="text-gray-900 text-xs font-medium">
                    {order.customerInfo.streetNumber}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 text-xs">
                    Shipping e-mail address:
                  </span>
                  <span className="text-gray-900 text-xs font-medium">
                    {order.customerInfo.email}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl overflow-hidden mb-8">
            <div className="hidden md:block">
              <div className="bg-[#E8E8E8] px-8 py-6 grid grid-cols-[3fr_1.5fr_0.9fr_0.9fr_0.9fr_0.6fr_0.5fr_0.7fr] gap-4 items-center font-poppins text-xs font-medium text-[#2B3133] uppercase">
                <div>SUPPLEMENT</div>
                <div>SUBSTANCE</div>
                <div>DOSAGE</div>
                <div>FORM</div>
                <div>FREQUENCY</div>
                <div className="text-left">NUMBER OF DAYS</div>
                <div className="text-center">QUANTITY</div>
                <div className="text-right">PRICE</div>
              </div>

              {order.supplements.map((supplement, idx) => (
                <div
                  key={supplement.id}
                  className={`px-8 py-6 grid grid-cols-[3fr_1.5fr_0.9fr_0.9fr_0.9fr_0.6fr_0.5fr_0.7fr] gap-4 items-center ${
                    idx !== order.supplements.length - 1
                      ? 'border-b border-[#EDEEF0]'
                      : ''
                  }`}
                >
                  <div className="flex items-center gap-3 font-poppins pr-4 border-r border-[#EDEEF0] relative">
                    <div className="w-14 h-14 bg-[#F5F5F5] rounded-lg flex items-center justify-center shrink-0">
                      {supplement.image === productImage ? (
                        <div className="w-full h-full bg-[#F5F5F5] rounded-lg" />
                      ) : (
                        <img
                          src={supplement.image}
                          alt={supplement.name}
                          className="w-full h-full object-contain p-2"
                        />
                      )}
                    </div>
                    <div
                      className="flex-1 relative"
                      ref={(el) => (supplementRefs.current[supplement.id] = el)}
                      onMouseEnter={(e) => {
                        if (supplement.webInfo) {
                          const h3Element = e.currentTarget.querySelector('h3');
                          const rect = h3Element
                            ? h3Element.getBoundingClientRect()
                            : e.currentTarget.getBoundingClientRect();
                          setTooltipPosition({
                            top: rect.bottom + 16,
                            left: rect.left,
                          });
                          setHoveredSupplementId(supplement.id);
                        }
                      }}
                      onMouseLeave={() => setHoveredSupplementId(null)}
                    >
                      <h3 className="font-semibold text-[#2B3133] text-sm mb-0.5 cursor-pointer hover:text-[#119975] transition-colors flex items-center gap-1">
                        {supplement.brand && supplement.brand !== 'N/A' && (
                          <>
                            {supplement.brand}{' '}
                          </>
                        )}
                        {supplement.name}
                        {supplement.isComplex && (
                          <img
                            src={badgeIcon}
                            alt="Complex"
                            className="w-5 h-5"
                          />
                        )}
                      </h3>
                      <p className="text-gray-500 text-xs">
                        {supplement.description}
                      </p>
                    </div>
                  </div>

                  <div className="text-[#2B3133] text-xs font-poppins">
                    {supplement.substance}
                  </div>

                  <div className="text-[#2B3133] text-xs font-poppins">
                    {supplement.dosage}
                  </div>

                  <div className="text-[#2B3133] text-xs font-poppins">
                    {supplement.form}
                  </div>

                  <div className="text-[#2B3133] text-xs font-poppins">
                    {supplement.frequency}
                  </div>

                  <div className="text-[#2B3133] text-xs font-poppins pr-4 border-r border-[#EDEEF0] flex items-center h-full">
                    {supplement.numberOfDays}
                  </div>

                  <div className="text-[#2B3133] text-xs text-center font-poppins">
                    {supplement.quantity}
                  </div>

                  <div className="text-[#2B3133] text-sm font-semibold text-right font-poppins">
                    {supplement.price}
                  </div>
                </div>
              ))}

              <div className="border-t border-[#EDEEF0] px-8 py-6">
                <div className="flex justify-between items-end gap-16">
                  {order.orderInfo.approval && (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleDownloadReport}
                        disabled={downloading}
                        className="flex items-center gap-2 text-gray-600 hover:text-[#119975] font-medium transition-colors font-poppins text-xs px-4 py-2 h-[48px] border border-gray-300 rounded-lg hover:border-[#119975] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 2V10M8 10L5.5 7.5M8 10L10.5 7.5M3 11V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V11"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>{downloading ? 'Exporting...' : 'Client invoice'}</span>
                      </button>
                      <button
                        onClick={handleDownloadPurchasingInvoice}
                        disabled={downloadingPurchasing}
                        className="flex items-center gap-2 text-gray-600 hover:text-[#119975] font-medium transition-colors font-poppins text-xs px-4 py-2 h-[48px] border border-gray-300 rounded-lg hover:border-[#119975] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 2V10M8 10L5.5 7.5M8 10L10.5 7.5M3 11V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V11"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>{downloadingPurchasing ? 'Exporting...' : 'Purchasing invoice'}</span>
                      </button>
                    </div>
                  )}
                  <div className="flex flex-col items-end gap-2 ml-auto">
                    <div className="flex items-center gap-4">
                      <span className="text-[#2B3133] font-poppins font-medium text-sm w-[340px] text-right whitespace-nowrap">
                        Verzendkosten en fulfilment:
                      </span>
                      <span className="text-[#2B3133] font-poppins font-medium text-sm w-[140px] text-right">
                        {formatEuroNl(shippingExVat)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-[#2B3133] font-poppins font-medium text-sm w-[180px] text-right">
                        BTW (21%):
                      </span>
                      <span className="text-[#2B3133] font-poppins font-medium text-sm w-[140px] text-right">
                        {formatEuroNl(vatOnShippingAndFulfilment)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-[#2B3133] font-poppins font-semibold text-xl w-[180px] text-right">
                        Total:
                      </span>
                      <span className="text-[#2B3133] font-poppins font-bold text-xl w-[140px] text-right">
                        {formatEuroNl(grandTotal)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-[#2B3133] font-poppins font-medium text-sm w-[180px] text-right">
                        Purchasing amount:
                      </span>
                      <span className="text-[#2B3133] font-poppins font-medium text-sm w-[140px] text-right">
                        €{order.purchasingAmount.toFixed(2).replace(/\./g, ',')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {!order.orderInfo.approval && (
                <div className="border-t border-[#EDEEF0] px-8 py-6">
                  <div className="flex justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleDownloadReport}
                        disabled={downloading}
                        className="flex items-center gap-2 text-gray-600 hover:text-[#119975] font-medium transition-colors font-poppins text-xs px-4 py-4 border border-gray-300 rounded-lg hover:border-[#119975] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 2V10M8 10L5.5 7.5M8 10L10.5 7.5M3 11V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V11"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>{downloading ? 'Exporting...' : 'Client invoice'}</span>
                      </button>
                      <button
                        onClick={handleDownloadPurchasingInvoice}
                        disabled={downloadingPurchasing}
                        className="flex items-center gap-2 text-gray-600 hover:text-[#119975] font-medium transition-colors font-poppins text-xs px-4 py-4 border border-gray-300 rounded-lg hover:border-[#119975] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8 2V10M8 10L5.5 7.5M8 10L10.5 7.5M3 11V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V11"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>{downloadingPurchasing ? 'Exporting...' : 'Purchasing invoice'}</span>
                      </button>
                    </div>
                    {!shouldHideApproveButton && (
                      <button
                        onClick={handleApproveClick}
                        disabled={approving}
                        className="bg-[#119975] text-white px-12 py-3 rounded-full font-poppins font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {approving ? 'Approving...' : 'Approve'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="md:hidden space-y-4 p-4">
              {order.supplements.map((supplement) => (
                <div
                  key={supplement.id}
                  className="bg-[#FBF6F2] rounded-2xl p-4 font-poppins"
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-14 h-14 bg-[#F5F5F5] rounded-lg flex items-center justify-center shrink-0">
                      {supplement.image === productImage ? (
                        <div className="w-full h-full bg-[#F5F5F5] rounded-lg" />
                      ) : (
                        <img
                          src={supplement.image}
                          alt={supplement.name}
                          className="w-full h-full object-contain p-2"
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#2B3133] text-sm mb-1 flex items-center gap-1">
                        {supplement.brand && supplement.brand !== 'N/A' && (
                          <>
                            {supplement.brand}{' '}
                          </>
                        )}
                        {supplement.name}
                        {supplement.isComplex && (
                          <img
                            src={badgeIcon}
                            alt="Complex"
                            className="w-5 h-5"
                          />
                        )}
                      </h3>
                      <p className="text-gray-500 text-xs">
                        {supplement.description}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between py-2 border-b border-[#EDEEF0]">
                      <span className="text-[#2B3133] text-sm font-medium">
                        Substance
                      </span>
                      <span className="text-[#2B3133] text-sm">
                        {supplement.substance}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#EDEEF0]">
                      <span className="text-[#2B3133] text-sm font-medium">
                        Dosage
                      </span>
                      <span className="text-[#2B3133] text-sm">
                        {supplement.dosage}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#EDEEF0]">
                      <span className="text-[#2B3133] text-sm font-medium">
                        Form
                      </span>
                      <span className="text-[#2B3133] text-sm">
                        {supplement.form}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#EDEEF0]">
                      <span className="text-[#2B3133] text-sm font-medium">
                        Frequency
                      </span>
                      <span className="text-[#2B3133] text-sm">
                        {supplement.frequency}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#EDEEF0]">
                      <span className="text-[#2B3133] text-sm font-medium">
                        Number of Days
                      </span>
                      <span className="text-[#2B3133] text-sm">
                        {supplement.numberOfDays}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#EDEEF0]">
                      <span className="text-[#2B3133] text-sm font-medium">
                        Quantity
                      </span>
                      <span className="text-[#2B3133] text-sm">
                        {supplement.quantity}
                      </span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span className="text-[#2B3133] text-sm font-medium">
                        Price
                      </span>
                      <span className="text-[#119975] text-base font-semibold">
                        {supplement.price}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="flex flex-col items-start gap-2 mb-4 w-full">
                  <div className="flex items-center justify-between gap-4 w-full">
                    <span className="text-[#2B3133] font-poppins text-sm font-medium">
                      Verzendkosten en fulfilment:
                    </span>
                    <span className="text-[#2B3133] font-poppins text-sm font-medium">
                      {formatEuroNl(shippingExVat)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 w-full">
                    <span className="text-[#2B3133] font-poppins text-sm font-medium">
                      BTW (21%):
                    </span>
                    <span className="text-[#2B3133] font-poppins text-sm font-medium">
                      {formatEuroNl(vatOnShippingAndFulfilment)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 w-full">
                    <span className="text-[#2B3133] font-poppins text-lg font-semibold">
                      Total:
                    </span>
                    <span className="text-[#2B3133] font-poppins text-lg font-semibold">
                      {formatEuroNl(grandTotal)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-4 w-full">
                    <span className="text-[#2B3133] font-poppins text-sm font-medium">
                      Purchasing amount:
                    </span>
                    <span className="text-[#2B3133] font-poppins text-sm font-medium">
                      €{order.purchasingAmount.toFixed(2).replace(/\./g, ',')}
                    </span>
                  </div>
                </div>
                {!order.orderInfo.approval && (
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={handleDownloadReport}
                      disabled={downloading}
                      className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-[#119975] font-medium transition-colors font-poppins text-xs py-3 border border-gray-300 rounded-lg hover:border-[#119975] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 2V10M8 10L5.5 7.5M8 10L10.5 7.5M3 11V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V11"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>{downloading ? 'Exporting...' : 'Client invoice'}</span>
                    </button>
                    <button
                      onClick={handleDownloadPurchasingInvoice}
                      disabled={downloadingPurchasing}
                      className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-[#119975] font-medium transition-colors font-poppins text-xs py-3 border border-gray-300 rounded-lg hover:border-[#119975] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 2V10M8 10L5.5 7.5M8 10L10.5 7.5M3 11V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V11"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>{downloadingPurchasing ? 'Exporting...' : 'Purchasing invoice'}</span>
                    </button>
                    {!shouldHideApproveButton && (
                      <button
                        onClick={handleApproveClick}
                        disabled={approving}
                        className="w-full bg-[#119975] text-white py-3 rounded-full font-poppins font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {approving ? 'Approving...' : 'Approve'}
                      </button>
                    )}
                  </div>
                )}
                {order.orderInfo.approval && (
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={handleDownloadReport}
                      disabled={downloading}
                      className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-[#119975] font-medium transition-colors font-poppins text-xs py-3 border border-gray-300 rounded-lg hover:border-[#119975] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 2V10M8 10L5.5 7.5M8 10L10.5 7.5M3 11V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V11"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>{downloading ? 'Exporting...' : 'Client invoice'}</span>
                    </button>
                    <button
                      onClick={handleDownloadPurchasingInvoice}
                      disabled={downloadingPurchasing}
                      className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-[#119975] font-medium transition-colors font-poppins text-xs py-3 border border-gray-300 rounded-lg hover:border-[#119975] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8 2V10M8 10L5.5 7.5M8 10L10.5 7.5M3 11V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V11"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>{downloadingPurchasing ? 'Exporting...' : 'Purchasing invoice'}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {hoveredSupplementId &&
        order?.supplements?.find((s) => s.id === hoveredSupplementId)
          ?.webInfo &&
        createPortal(
          <div
            className="fixed z-[9999] w-[500px] max-w-[90vw] bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-h-[70vh] overflow-y-auto"
            style={{
              top: `${tooltipPosition.top}px`,
              left: `${tooltipPosition.left}px`,
            }}
            onMouseEnter={() => setHoveredSupplementId(hoveredSupplementId)}
            onMouseLeave={() => setHoveredSupplementId(null)}
          >
            <div
              className="text-sm text-[#2B3133] font-poppins tooltip-content"
              dangerouslySetInnerHTML={{
                __html: stripWebInfoTitle(
                  order.supplements.find((s) => s.id === hoveredSupplementId)
                    ?.webInfo || ''
                ),
              }}
              style={{
                fontSize: '13px',
                lineHeight: '1.6',
              }}
            />
            <style>{`
            .tooltip-content table {
              width: 100%;
              border-collapse: collapse;
              margin: 8px 0;
            }
            .tooltip-content table td,
            .tooltip-content table th {
              border: 1px solid #ddd;
              padding: 6px;
              text-align: left;
            }
            .tooltip-content table strong {
              font-weight: 600;
            }
            .tooltip-content b {
              font-weight: 600;
            }
            .tooltip-content p {
              margin: 8px 0;
            }
          `}</style>
          </div>,
          document.body
        )}

      {showConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={handleCancel}
        >
          <div
            className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-[#2B3133] font-poppins font-semibold text-xl mb-4">
              Confirm Approval
            </h2>
            <p className="text-[#2B3133] font-poppins text-sm mb-6">
              Are you sure you want to approve this order? You will not be able
              to edit the order after approval.
            </p>
            <div className="flex gap-4 justify-end">
              <button
                onClick={handleCancel}
                disabled={approving}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-full font-poppins font-medium hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={approving}
                className="px-6 py-2.5 bg-[#119975] text-white rounded-full font-poppins font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {approving ? 'Approving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
